import { PDFDocument, PDFPage, rgb, pushGraphicsState, popGraphicsState, setTextRenderingMode, setStrokingColor, setLineWidth, TextRenderingMode } from 'pdf-lib'
import fontkit from '@pdf-lib/fontkit'
import { useCopybookStore, type GridType, getGridChars, getEffectiveLayout, FONT_GRID_RATIO } from '../store/copybookStore'

const PAPER_SIZES: Record<string, { width: number; height: number }> = {
  A4P: { width: 595.28, height: 841.89 },
  A4L: { width: 841.89, height: 595.28 },
  A3P: { width: 841.89, height: 1190.55 },
  A3L: { width: 1190.55, height: 841.89 },
  A5P: { width: 419.53, height: 595.28 },
  A5L: { width: 595.28, height: 419.53 },
  B4P: { width: 708.66, height: 1000.63 },
  B4L: { width: 1000.63, height: 708.66 },
  B5P: { width: 498.90, height: 708.66 },
  B5L: { width: 708.66, height: 498.90 },
  // Backward compatibility
  B5: { width: 498.90, height: 708.66 },
}

function mmToPt(mm: number): number {
  return mm * 2.83465
}

function getPdfFontUrl(fontFamily: string): string {
  if (fontFamily.includes('演示夏行楷') || fontFamily.includes('Slidexiaxing')) {
    return '/fonts/Slidexiaxing-Regular.ttf'
  }
  if (fontFamily.includes('The Peak Font Plus')) {
    return '/fonts/ThePeakFontPlus-Regular.ttf'
  }

  return '/fonts/ThePeakFontPlus-Regular.ttf'
}

export async function generatePDF() {
  const store = useCopybookStore.getState()
  const paper = PAPER_SIZES[store.paperSize] || PAPER_SIZES.A4P

  const pdfDoc = await PDFDocument.create()
  pdfDoc.registerFontkit(fontkit)
  
  const fontUrl = getPdfFontUrl(store.fontFamily)
  const fontBytes = await fetch(fontUrl).then(res => {
    if (!res.ok) throw new Error('Failed to load PDF font')
    return res.arrayBuffer()
  })
  const font = await pdfDoc.embedFont(fontBytes)

  const layout = getEffectiveLayout({
    paperSize: store.paperSize,
    marginTop: store.marginTop,
    marginBottom: store.marginBottom,
    marginLeft: store.marginLeft,
    marginRight: store.marginRight,
    gridSize: store.gridSize,
    colGap: store.colGap,
    rowGap: store.rowGap,
    colsPerRow: store.colsPerRow,
    printTitleAndSource: store.printTitleAndSource,
    title: store.title,
    subtitle: store.subtitle,
    autoFill: store.autoFill,
  })

  const marginTop = mmToPt(store.marginTop)
  const marginBottom = mmToPt(store.marginBottom)
  const marginLeft = mmToPt(store.marginLeft)
  const marginRight = mmToPt(store.marginRight)

  const contentWidth = paper.width - marginLeft - marginRight

  // 當啟用描紅或間隔臨帖時，依據換行與間隔排版解析字元
  const lineLength = store.fontDirection === 'vertical' ? layout.rows : layout.cols
  const chars = getGridChars(store.text, {
    preserveLineBreaks: store.preserveLineBreaks,
    lineLength,
    copyInterval: store.copyInterval,
    copyBlankCount: store.copyBlankCount,
    copyRowInterval: store.copyRowInterval,
    copyRowBlankCount: store.copyRowBlankCount,
    showTrace: store.showTrace,
    traceInterval: store.traceInterval,
  })
  const gridSize = mmToPt(layout.gridSize)
  const colGap = mmToPt(layout.colGap)
  const rowGap = mmToPt(layout.rowGap)
  const cols = layout.cols
  const maxRowsPerPage = layout.rows
  const cellsPerPage = maxRowsPerPage * cols

  const totalColWidth = cols * gridSize + (cols - 1) * colGap
  const startX = marginLeft + (contentWidth - totalColWidth) / 2
  const headerHeight = store.printTitleAndSource && (store.title || store.subtitle) ? 20 : 0
  const startY = paper.height - marginTop - mmToPt(headerHeight) - gridSize
  const pageChars = buildPdfPages(chars, cellsPerPage, store.fillLastPage, store.showCorrection)

  pageChars.forEach((charsOnPage, pageIdx) => {
    const page = pdfDoc.addPage([paper.width, paper.height])

    if (store.printTitleAndSource) {
      if (store.title) {
        const titleWidth = font.widthOfTextAtSize(store.title, 16)
        page.drawText(store.title, {
          x: (paper.width - titleWidth) / 2,
          y: paper.height - marginTop - 16,
          size: 16,
          font,
          color: rgb(0.07, 0.09, 0.15),
        })
      }

      if (store.subtitle) {
        const subWidth = font.widthOfTextAtSize(store.subtitle, 10)
        page.drawText(store.subtitle, {
          x: (paper.width - subWidth) / 2,
          y: paper.height - marginTop - 31,
          size: 10,
          font,
          color: rgb(0.4, 0.4, 0.4),
        })
      }
    }

    charsOnPage.forEach((_, sourceIdx) => {
      const cellIdx = getPdfCellIndex(sourceIdx, cols, maxRowsPerPage, store.fontDirection)
      const char = charsOnPage[sourceIdx]
      const row = Math.floor(cellIdx / cols)
      const col = cellIdx % cols
      const x = startX + col * (gridSize + colGap)
      const y = startY - row * (gridSize + rowGap)

      // PDF points conversion can leave the last row a fraction below the bottom margin.
      // Keep a small tolerance so the final row is not dropped at page boundaries.
      if (y < marginBottom - 0.5) return

      const isLastCol = col === cols - 1
      const nextRowY = startY - (row + 1) * (gridSize + rowGap)
      const isLastRow = row === maxRowsPerPage - 1 || (nextRowY < marginBottom - 0.5)

      drawGridPDF(page, store.gridType, x, y, gridSize, store.gridOpacity / 100, store.gridLineWidth, store.gridColor, {
        isIsolated: colGap > 0 && rowGap > 0,
        drawRight: colGap > 0 || isLastCol,
        drawBottom: rowGap > 0 || isLastRow,
      })

      if (!char) return

      const absoluteIndex = pageIdx * cellsPerPage + sourceIdx
      const isTrace = store.showTrace
      const color = isTrace ? hexToRgb(store.traceColor) : hexToRgb(store.exampleColor)
      const fontSize = gridSize * FONT_GRID_RATIO
      const charWidth = font.widthOfTextAtSize(char, fontSize)
      const textHeight = font.heightAtSize(fontSize)
      const textHeightWithoutDescender = font.heightAtSize(fontSize, { descender: false })
      const descenderOffset = textHeight - textHeightWithoutDescender

      const isHollow = isTrace && (
        store.traceInterval 
          ? (absoluteIndex % 2 === 1) 
          : (store.traceType === 'hollow')
      )

      if (isHollow) {
        const strokeWidth = store.traceLineWidth * 0.75
        page.pushOperators(
          pushGraphicsState(),
          setStrokingColor(color),
          setTextRenderingMode(TextRenderingMode.Outline),
          setLineWidth(strokeWidth)
        )
      }

      page.drawText(char, {
        x: x + (gridSize - charWidth) / 2,
        y: y + (gridSize - textHeight) / 2 + descenderOffset,
        size: fontSize,
        font,
        color,
      })

      if (isHollow) {
        page.pushOperators(popGraphicsState())
      }
    })
  })

  // Page numbers
  if (store.showPageNumber) {
    const pages = pdfDoc.getPages()
    pages.forEach((p, idx) => {
      const pageNum = String(idx + 1)
      const pw = font.widthOfTextAtSize(pageNum, 10)
      p.drawText(pageNum, {
        x: (paper.width - pw) / 2,
        y: marginBottom / 2,
        size: 10,
        font,
        color: rgb(0.6, 0.6, 0.6),
      })
    })
  }

  // Footer
  if (store.footer) {
    const pages = pdfDoc.getPages()
    const fw = font.widthOfTextAtSize(store.footer, 10)
    pages.forEach((p) => {
      p.drawText(store.footer, {
        x: (paper.width - fw) / 2,
        y: marginBottom / 2 - 12,
        size: 10,
        font,
        color: rgb(0.4, 0.4, 0.4),
      })
    })
  }

  const pdfBytes = await pdfDoc.save()
  const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = `字帖_${store.title || '練習'}.pdf`
  link.click()

  URL.revokeObjectURL(url)
}

function getPdfCellIndex(sourceIdx: number, cols: number, rows: number, direction: string): number {
  if (direction !== 'vertical') {
    return sourceIdx
  }

  const sourceCol = Math.floor(sourceIdx / rows)
  const row = sourceIdx % rows
  const visualCol = cols - 1 - sourceCol
  return row * cols + visualCol
}

interface DrawGridOptions {
  isIsolated: boolean
  drawRight: boolean
  drawBottom: boolean
}

function drawGridPDF(
  page: PDFPage,
  type: GridType,
  x: number,
  y: number,
  size: number,
  opacity: number,
  lineWidthPx: number,
  gridColorHex: string = '#9ca3af',
  options: DrawGridOptions = { isIsolated: true, drawRight: true, drawBottom: true }
) {
  // 將 px 單位換算為 PDF 點 (pt)，1 px = 0.75 pt
  const lineWidth = lineWidthPx * 0.75
  const baseColor = hexToRgb(gridColorHex || '#9ca3af')
  const r = 1 - opacity * (1 - baseColor.red)
  const g = 1 - opacity * (1 - baseColor.green)
  const b = 1 - opacity * (1 - baseColor.blue)
  const color = rgb(r, g, b)

  // 外邊框繪製：若格子之間有間隙則完整繪製矩形；無間隙時分邊繪製，避免相鄰邊界重疊導致粗細與透明度不均
  if (options.isIsolated) {
    page.drawRectangle({
      x,
      y,
      width: size,
      height: size,
      borderColor: color,
      borderWidth: lineWidth,
    })
  } else {
    // 上邊
    page.drawLine({
      start: { x, y: y + size },
      end: { x: x + size, y: y + size },
      thickness: lineWidth,
      color,
    })
    // 左邊
    page.drawLine({
      start: { x, y },
      end: { x, y: y + size },
      thickness: lineWidth,
      color,
    })

    // 右邊（僅在有欄間距或是最後一欄時繪製）
    if (options.drawRight) {
      page.drawLine({
        start: { x: x + size, y },
        end: { x: x + size, y: y + size },
        thickness: lineWidth,
        color,
      })
    }

    // 下邊（僅在有列間距或是最後一列時繪製）
    if (options.drawBottom) {
      page.drawLine({
        start: { x, y },
        end: { x: x + size, y },
        thickness: lineWidth,
        color,
      })
    }
  }

  const half = size / 2

  // 內部輔助線（使用 dashArray 呈現與網頁預覽一致的虛線效果）
  switch (type) {
    case 'tian':
      page.drawLine({ start: { x, y: y + half }, end: { x: x + size, y: y + half }, thickness: lineWidth, color, dashArray: [2, 2] })
      page.drawLine({ start: { x: x + half, y }, end: { x: x + half, y: y + size }, thickness: lineWidth, color, dashArray: [2, 2] })
      break
    case 'mi':
      page.drawLine({ start: { x, y: y + half }, end: { x: x + size, y: y + half }, thickness: lineWidth, color, dashArray: [2, 2] })
      page.drawLine({ start: { x: x + half, y }, end: { x: x + half, y: y + size }, thickness: lineWidth, color, dashArray: [2, 2] })
      page.drawLine({ start: { x, y }, end: { x: x + size, y: y + size }, thickness: lineWidth, color, dashArray: [2, 2] })
      page.drawLine({ start: { x: x + size, y }, end: { x, y: y + size }, thickness: lineWidth, color, dashArray: [2, 2] })
      break
    case 'huigong':
    {
      const pad = size * 0.2
      page.drawRectangle({
        x: x + pad,
        y: y + pad,
        width: size - pad * 2,
        height: size - pad * 2,
        borderColor: color,
        borderWidth: lineWidth,
        borderDashArray: [2, 2],
      })
      page.drawLine({ start: { x, y: y + half }, end: { x: x + size, y: y + half }, thickness: lineWidth, color, dashArray: [2, 2] })
      page.drawLine({ start: { x: x + half, y }, end: { x: x + half, y: y + size }, thickness: lineWidth, color, dashArray: [2, 2] })
      break
    }
    case 'jiugong':
    {
      const third = size / 3
      for (let i = 1; i < 3; i++) {
        page.drawLine({ start: { x, y: y + third * i }, end: { x: x + size, y: y + third * i }, thickness: lineWidth, color, dashArray: [2, 2] })
        page.drawLine({ start: { x: x + third * i, y }, end: { x: x + third * i, y: y + size }, thickness: lineWidth, color, dashArray: [2, 2] })
      }
      break
    }
  }
}

function buildPdfPages(chars: string[], cellsPerPage: number, fillLastPage: boolean, showCorrection: boolean): string[][] {
  if (cellsPerPage <= 0) return []

  const pages: string[][] = []
  let current: string[] = []

  chars.forEach((char) => {
    current.push(char)
    if (current.length === cellsPerPage) {
      pages.push(current)
      current = []
    }
  })

  if (fillLastPage && (current.length > 0 || pages.length === 0)) {
    current.push(...Array(cellsPerPage - current.length).fill(''))
    pages.push(current)
    current = []
  }

  if (showCorrection) {
    if (current.length > 0) {
      current.push(...Array(Math.min(2, cellsPerPage - current.length)).fill(''))
      current.push(...Array(cellsPerPage - current.length).fill(''))
      pages.push(current)
      current = []
    } else if (pages.length === 0) {
      current.push('', '')
      current.push(...Array(cellsPerPage - current.length).fill(''))
      pages.push(current)
    }
  } else if (current.length > 0) {
    current.push(...Array(cellsPerPage - current.length).fill(''))
    pages.push(current)
  }

  if (pages.length === 0) {
    pages.push(Array(cellsPerPage).fill(''))
  }

  return pages
}

function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? rgb(
        parseInt(result[1], 16) / 255,
        parseInt(result[2], 16) / 255,
        parseInt(result[3], 16) / 255
      )
    : rgb(0, 0, 0)
}
